import { getAccessToken, getChatBase } from '../../../AiAssistant/chatApiBase'
import {
  SpendBreakdown,
  SpendBucketSize,
  SpendFilters,
  SpendGrouping,
  SpendSummary,
  SpendTimeseries,
  buildSpendParams,
} from './spendModel'

/**
 * Reads the chat API directly rather than the shared axios instance, matching
 * chatSearchApi.ts: the chat API is a separate service on its own host with
 * its own bearer token, not the main API.
 */
async function getSpend<T>(path: string, params: URLSearchParams): Promise<T> {
  const token = getAccessToken()
  const res = await fetch(`${getChatBase()}/spend/${path}?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    throw new Error(await describeFailure(res))
  }
  return res.json()
}

/**
 * Surface what the API said, not just the status code.
 *
 * The window and allowlist rules live server-side and are not restated in this
 * app, so its message ("hourly buckets require a window of 31 days or less")
 * is the only accurate explanation available. A 403 is called out by name
 * because it means a permissions problem, which must never be mistaken for a
 * period of no spend.
 */
async function describeFailure(res: Response): Promise<string> {
  if (res.status === 403) {
    return 'Spend reporting is restricted to superusers.'
  }
  try {
    const body = await res.json()
    const detail = body?.detail
    if (typeof detail === 'string') {
      return detail
    }
    if (Array.isArray(detail) && typeof detail[0]?.msg === 'string') {
      return detail[0].msg
    }
  } catch {
    /* fall through to the status code */
  }
  return `Request failed: ${res.status}`
}

export function getSpendSummary(filters: SpendFilters): Promise<SpendSummary> {
  return getSpend<SpendSummary>('summary', buildSpendParams(filters))
}

export function getSpendTimeseries(
  filters: SpendFilters,
  bucket: SpendBucketSize,
): Promise<SpendTimeseries> {
  return getSpend<SpendTimeseries>('timeseries', buildSpendParams(filters, { bucket }))
}

export function getSpendBreakdown(
  filters: SpendFilters,
  by: SpendGrouping,
  limit: number,
): Promise<SpendBreakdown> {
  return getSpend<SpendBreakdown>(
    'breakdown',
    buildSpendParams(filters, { by, limit: String(limit) }),
  )
}
